import { useState } from "react";
import { Box, Chip, Collapse, Paper, Typography } from "@mui/material";

const sortAssets = (assets, investByDate, guidanceMap) => {
	if (investByDate) {
		return [...assets].sort((a, b) => {
			const da = guidanceMap[a.identificator]?.lastPurchaseDate ?? null
			const db = guidanceMap[b.identificator]?.lastPurchaseDate ?? null
			if (da === null && db === null) return 0
			if (da === null) return -1
			if (db === null) return 1
			return new Date(da) - new Date(db)
		})
	}
	return [...assets].sort((a, b) => a.diff - b.diff)
}

const NextAssetFull = ({ allocations = [], portfolio = [], guidanceMap = {}, investByDate = false }) => {
	const [showExcluded, setShowExcluded] = useState(false)

	const ranked = allocations
		.filter(item => typeof item.current !== 'undefined' && typeof item.planned !== 'undefined' && item.planned !== 0)
		.map(item => ({
			...item,
			diff: (Number(item.current) || 0) - (Number(item.planned) || 0)
		}))
		.sort((a, b) => a.diff - b.diff)

	const rows = ranked.map(allocation => {
		const assets = portfolio
			.filter(p => p.assetType === allocation.assetType && p.target && Number(p.target) > 0)
			.map(p => ({
				...p,
				diff: (Number(p.percentage) || 0) - (Number(p.target) || 0)
			}))

		return {
			key		: allocation.assetType,
			label	: allocation.label,
			current	: Number(allocation.current) || 0,
			planned	: Number(allocation.planned) || 0,
			diff	: Number(allocation.diff) || 0,
			assets	: sortAssets(assets, investByDate, guidanceMap)
		}
	})

	const totalExcluded = rows.reduce((acc, row) => acc + row.assets.filter(a => !a.investable).length, 0)

	const diffColor = (value) => value > 0 ? 'success.main' : value < 0 ? 'error.main' : 'text.secondary'
	const renderDiffPercent = (value) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`

	return (
		<Box display="grid" gap={2}>
			{rows.map(row => (
				<Paper
					key={row.key}
					variant="outlined"
					sx={{ padding: 1.5, borderRadius: 1.5, borderColor: 'rgba(25,118,210,0.3)' }}
				>
					<Box marginBottom={1}>
						<Typography variant="subtitle1" fontWeight={700} color="primary">
							{row.label}
						</Typography>
						<Typography variant="body2">
							Dif. atual vs alvo:{' '}
							<Box component="span" sx={{ color: diffColor(row.diff), fontWeight: 600 }}>
								{renderDiffPercent(row.diff)}
							</Box>
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Atual: {row.current}% • Alvo: {row.planned}%
						</Typography>
					</Box>

					{row.assets.length > 0 ? (
						<Box display="grid" gap={1}>
							{row.assets.map(asset => {
								const excluded = !asset.investable

								if (excluded && !showExcluded) return null

								return (
									<Box
										key={asset.stockTicker || asset.identificator}
										sx={{
											border		: '1px dashed',
											borderColor	: excluded ? 'divider' : '#bdbdbd',
											borderRadius: 1,
											padding		: 1,
											bgcolor		: excluded ? 'transparent' : '#fafafa',
										}}
									>
										<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
											<Typography variant="body1" color={excluded ? 'text.disabled' : 'primary'} fontWeight={700}>
												{asset.identificator || asset.stockTicker}
											</Typography>
											{excluded && (
												<Chip
													label={asset.excludedReason}
													size="small"
													color="warning"
													sx={{ fontSize: '0.65rem', height: 20 }}
												/>
											)}
										</Box>
										<Typography variant="body2" color={excluded ? 'text.disabled' : 'text.primary'}>
											Dif. atual vs alvo:{' '}
											<Box component="span" sx={{ color: excluded ? 'text.disabled' : diffColor(Number(asset.diff) || 0), fontWeight: 600 }}>
												{renderDiffPercent(Number(asset.diff) || 0)}
											</Box>
										</Typography>
										<Typography variant="body2" color={excluded ? 'text.disabled' : 'text.secondary'}>
											Atual: {(Number(asset.percentage) || 0).toFixed(2)}% • Alvo: {asset.target}%
										</Typography>
									</Box>
								)
							})}
						</Box>
					) : (
						<Typography variant="caption" color="text.secondary">Nenhum ticker com target definido nesta classe.</Typography>
					)}
				</Paper>
			))}

			<Box display="flex" flexDirection="column" gap={1} marginTop={1}>
				{totalExcluded > 0 && (
					<Typography
						variant="caption"
						color="primary"
						sx={{ cursor: 'pointer', textDecoration: 'underline', alignSelf: 'center' }}
						onClick={() => setShowExcluded(v => !v)}
					>
						{showExcluded
							? `Ocultar removidos (${totalExcluded})`
							: `Ver removidos pelas regras de aporte (${totalExcluded})`}
					</Typography>
				)}
				<Typography variant="caption" color="text.secondary" textAlign="center">
					{investByDate
						? 'Ordenado pela classe mais distante do alvo e, dentro da classe, pelo ativo sem aporte há mais tempo.'
						: 'Ordenado pela maior distância do alvo (classe) e, dentro da classe, pelo maior gap do ticker.'}
				</Typography>
			</Box>
		</Box>
	)
}

export default NextAssetFull
